import { Fragment } from "react"

import { CheckIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { FeatureCard } from "@/embed/components/landing-page/FeatureCard"
import { cn } from "@/lib/utils"

/** Static marketing copy; rows are [left column, right column] pairs. */
const CAPITAL_PRODUCT_ROWS: [string, string][] = [
  ["Line of Credit", "Equipment Finance"],
  ["Term Loan", "SBA Loan"],
  ["Invoice Factoring", "Revenue-Based Financing"],
  ["Inventory Consignment", "and more..."],
]

export function LandingPage({
  onGetStartedClick,
  onSignInClick,
}: {
  onGetStartedClick: () => void
  onSignInClick: () => void
}) {
  return (
    <Card className="flex gap-0 overflow-auto p-0">
      <div className="px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="text-muted-foreground text-xs font-medium">
            <slot name="partner-logo"></slot>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onSignInClick}>
              Sign In
            </Button>
            <Button size="sm" onClick={onGetStartedClick}>
              Get Started
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="flex flex-1 flex-col gap-4 bg-gray-100/50">
        <div className="grid gap-4 p-6 md:grid-cols-2 md:items-center">
          <div className="flex flex-col gap-3">
            <h1 className="text-2xl leading-tight font-bold">
              Intelligent Funding for Founders Like You.
            </h1>

            <p className="text-muted-foreground text-sm text-balance">
              Access an AI-driven marketplace with 150+ lenders: SMB loans,
              private credit, commercial banks, and venture debt, all in one
              place.
            </p>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="bg-primary/10 text-primary flex size-4 items-center justify-center rounded-full">
                  <CheckIcon className="size-3" />
                </div>
                <span className="text-xs">
                  Customized solutions with AI-driven matching
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-primary/10 text-primary flex size-4 items-center justify-center rounded-full">
                  <CheckIcon className="size-3" />
                </div>
                <span className="text-xs">
                  Access to 150+ lenders with one profile
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-primary/10 text-primary flex size-4 items-center justify-center rounded-full">
                  <CheckIcon className="size-3" />
                </div>
                <span className="text-xs">Works for any size or industry</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4">
              <Button onClick={onGetStartedClick}>Get Started</Button>
            </div>
          </div>

          <Card className="@container min-h-[200px] w-full justify-center gap-3 p-4">
            <div className="text-muted text-xs font-bold tracking-wide uppercase">
              Capital available through this marketplace
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-x-4 @xl:grid-cols-[1fr_auto_1fr_auto]">
              <div className="text-muted hidden border-b px-1 pb-1.5 text-[11px] font-bold tracking-wide uppercase @xl:block">
                Product Type
              </div>
              <div className="text-muted hidden border-b px-1 pb-1.5 text-center text-[11px] font-bold tracking-wide uppercase @xl:block">
                Supported
              </div>
              <div className="text-muted border-b px-1 pb-1.5 text-[11px] font-bold tracking-wide uppercase">
                Product Type
              </div>
              <div className="text-muted border-b px-1 pb-1.5 text-center text-[11px] font-bold tracking-wide uppercase">
                Supported
              </div>
              {CAPITAL_PRODUCT_ROWS.map(([left, right], index) => {
                const rowBorder =
                  index < CAPITAL_PRODUCT_ROWS.length - 1 && "border-b"
                return (
                  <Fragment key={left}>
                    <div
                      className={cn(
                        "hidden px-1 py-1.5 text-[13px] @xl:block",
                        rowBorder,
                      )}
                    >
                      {left}
                    </div>
                    <div
                      className={cn(
                        "hidden items-start justify-center px-1 pt-2 pb-1.5 @xl:flex",
                        rowBorder,
                      )}
                    >
                      <CheckIcon
                        className="text-success size-3.5"
                        aria-hidden="true"
                      />
                    </div>
                    <div
                      className={cn(
                        "px-1 py-1.5 text-[13px]",
                        right === "and more..." && "text-muted italic",
                        rowBorder,
                      )}
                    >
                      {right}
                    </div>
                    <div
                      className={cn(
                        "flex items-start justify-center px-1 pt-2 pb-1.5",
                        rowBorder,
                      )}
                    >
                      <CheckIcon
                        className="text-success size-3.5"
                        aria-hidden="true"
                      />
                    </div>
                  </Fragment>
                )
              })}
            </div>
          </Card>
        </div>

        {/* Features Section */}
        <div className="grid gap-4 md:grid-cols-3">
          <FeatureCard
            title="Smarter matches"
            content="Our platform analyzes hundreds of financing products to match you with the right lender and structure."
          />
          <FeatureCard
            title="Speed to term sheet"
            content="Streamlined document collection and underwriting to move from intro to offers quickly."
          />
          <FeatureCard
            title="Transparent guidance"
            content="Side by side comparisons so you always pick the best option for your business."
          />

          <Card className="md:col-span-3">
            <CardContent className="flex flex-col justify-between md:flex-row">
              <div>
                <h2 className="text-base font-semibold">
                  See your matches in minutes
                </h2>
                <p className="text-muted-foreground text-xs">
                  Answer a few questions to unlock your options.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={onGetStartedClick}>
                  Get Started
                </Button>
                <Button variant="outline" size="sm" onClick={onSignInClick}>
                  Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="text-muted my-4 place-content-end self-center text-xs">
          Powered by <span className="font-semibold">boundless</span>
        </div>
      </CardContent>
    </Card>
  )
}
